import axios, { AxiosInstance } from 'axios';
import { environment } from '../config/environment';

export class ApiService {
  private httpClient: AxiosInstance;
  public selectedLanguage: any = { value: 'en', label: 'label-title.english' };
  public powerBy: any = environment.powerBy;
  public phoneCodeList: string[] = [
    '+1', '+1242', '+1246', '+1268', '+213', '+216', '+220', '+221',
    '+222', '+223', '+224', '+225', '+226', '+227', '+228', '+229',
    '+230', '+231', '+232', '+233', '+234', '+235', '+236', '+237',
    '+238', '+239', '+240', '+241', '+242', '+243', '+244', '+245',
    '+246', '+247', '+248', '+249', '+250', '+251', '+252', '+253',
    '+254', '+255', '+256', '+257', '+258', '+260', '+261', '+262',
    '+263', '+264', '+265', '+266', '+267', '+268', '+269', '+27',
    '+290', '+291', '+297', '+298', '+299', '+30', '+31', '+32',
    '+33', '+34', '+350', '+351', '+352', '+353', '+354', '+355',
    '+356', '+357', '+358', '+359', '+36', '+370', '+371', '+372',
    '+373', '+374', '+375', '+376', '+377', '+378', '+379', '+380',
    '+381', '+382', '+383', '+385', '+386', '+387', '+389', '+39',
    '+40', '+41', '+420', '+421', '+423', '+43', '+44', '+45',
    '+46', '+47', '+48', '+49', '+500', '+501', '+502', '+503',
    '+504', '+505', '+506', '+507', '+508', '+509', '+51', '+52',
    '+53', '+54', '+55', '+56', '+57', '+58', '+590', '+591',
    '+592', '+593', '+594', '+595', '+596', '+597', '+598', '+599',
    '+60', '+61', '+62', '+63', '+64', '+65', '+66', '+670',
    '+672', '+673', '+674', '+675', '+676', '+677', '+678', '+679',
    '+680', '+681', '+682', '+683', '+685', '+686', '+687', '+688',
    '+689', '+690', '+691', '+692', '+7', '+81', '+82', '+84',
    '+850', '+852', '+853', '+855', '+856', '+86', '+870', '+872',
    '+873', '+874', '+878', '+880', '+881', '+882', '+883', '+886',
    '+90', '+91', '+92', '+93', '+94', '+95', '+960', '+961',
    '+962', '+963', '+964', '+965', '+966', '+967', '+968', '+970',
    '+971', '+972', '+973', '+974', '+975', '+976', '+977', '+98',
    '+992', '+993', '+994', '+995', '+996', '+998'
  ];

  constructor() {
    this.httpClient = axios.create({
      baseURL: environment.API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  getHeaders(params: any = null) {
    const config: any = {
      headers: {},
    };
    if (params) {
      config.params = params;
    }
    return config;
  }

  async getData(url: string, params: any = null): Promise<any> {
    try {
      const response = await this.httpClient.get(url, this.getHeaders(params));
      return response.data;
    } catch (error) {
      console.error('API GET Error:', error);
      throw error;
    }
  }

  async postOrderData(url: string, data: any): Promise<any> {
    try {
      const response = await this.httpClient.post(url, data, this.getHeaders());
      return response.data;
    } catch (error) {
      console.error('API POST Error:', error);
      throw error;
    }
  }

  rederScript(setting: any): void {
    const detail = setting?.data?.bookingToolDetail;
    if (!detail) return;

    const scripts = [
      detail.marketingScript1,
      detail.marketingScript2,
      detail.marketingScript3,
      detail.marketingScript4
    ];

    // With booking attribution on, the app loads GA4 itself and conversions are sent server-side.
    // Google Analytics / Google Ads code in Scripts 1-4 would count twice (and the Ads conversion
    // would fire on every open), so it is skipped. Other pixels still run.
    const skipGoogleTags = detail.attributionEnabled === true;
    const googleTagRe = /googletagmanager\.com|google-analytics\.com|googleadservices\.com|\bgtag\s*\(/i;

    scripts.forEach((scriptHtml) => {
      if (!scriptHtml || typeof scriptHtml !== 'string') return;
      const value = scriptHtml.trim();
      if (!value) return;
      if (skipGoogleTags && googleTagRe.test(value)) {
        console.warn('Booking attribution is on: skipped Google tag in marketing script (remove it in the panel).');
        return;
      }

      try {
        // 1) Only a src URL (the admin hint for Script 1: "Only enter src")
        if (/^https?:\/\/\S+$/i.test(value)) {
          this.appendScript({ src: value, async: 'true' }, '');
          return;
        }

        // 2) Raw JavaScript without <script> tags (the admin hints for Scripts 2-4).
        //    Previously these were silently ignored.
        if (!/<script[\s>]/i.test(value)) {
          if (/^</.test(value)) return; // other HTML (noscript, img pixels...) is not executed
          this.appendScript({}, value);
          return;
        }

        // 3) Full <script> tags
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = value;
        tempDiv.querySelectorAll('script').forEach((parsedScript) => {
          const attrs: { [k: string]: string } = {};
          Array.from(parsedScript.attributes).forEach((attr) => { attrs[attr.name] = attr.value; });
          this.appendScript(attrs, parsedScript.innerHTML);
        });
      } catch (error) {
        console.error('Error inserting marketing script:', error);
      }
    });
  }

  /** Append a script to <head> once (deduplicated by src, or by inline content). */
  private appendScript(attrs: { [k: string]: string }, inlineContent: string): void {
    const src = attrs['src'];
    const content = (inlineContent || '').trim();
    if (src) {
      if (document.head.querySelector(`script[src="${src}"]`)) return;
    } else {
      if (!content) return;
      const exists = Array.from(document.querySelectorAll('script')).some(
        (s) => !s.getAttribute('src') && s.innerHTML.trim() === content
      );
      if (exists) return;
    }
    const newScript = document.createElement('script');
    Object.keys(attrs).forEach((name) => newScript.setAttribute(name, attrs[name]));
    if (content) newScript.innerHTML = content;
    document.head.appendChild(newScript);
  }
  roundFun(num: number): string {
    return this.selectedLanguage.value === 'nl'
      ? num.toFixed(2).replace('.', ',')
      : num.toFixed(2);
  }
  roundFunReturn(total: number, totalReturn: number): string {
    return this.selectedLanguage.value === 'nl'
      ? (total + totalReturn).toFixed(2).replace('.', ',')
      : (total + totalReturn).toFixed(2);
  }
}

export const apiService = new ApiService();
